/* eslint-disable no-underscore-dangle, no-magic-numbers */
/* global L */

import 'expose-loader?L!leaflet';
import _ from 'lodash';
import interact from 'interact.js';

import { isBoundsInsideArea } from '../../../../../helpers/leaflet-helpers';

import store from '../../../../../store/index';

let visiblePicAreas = [];

const SelectionBox = L.Layer.extend({
  initialize(latlng, { plot, enableSelection }) {
    this._latlng = latlng;
    this.plotId = plot.id;
    this.areaId = plot.areaId;
    this.plotSize = plot.sideLength;
    this._enableSelection = enableSelection;
    this._facesIds = [];
  },
  onAdd(map) {
    this._map = map;
    this.meterSize = this._getMeterWidthInPixels();
    this._el = L.DomUtil.create('div', 'selected-box leaflet-zoom-hide');
    this._el.dataset.size = this.plotSize * this.plotSize;

    this._el.addEventListener('mouseover', () => {
      map.dragging.disable();
    });

    this._el.addEventListener('mouseout', () => {
      map.dragging.enable();
    });

    if (this._enableSelection) {
      this._resizableHandle = L.DomUtil.create('span', 'selected-box__handle', this._el);
      this._updateHandleSize();

      interact(this._el)
        .on('tap', (event) => {
          this.fireEvent('click', event);
        })
        .resizable({
          squareResize: true,
          edges: {
            top: false,
            left: false,
            bottom: '.selected-box__handle',
            right: '.selected-box__handle',
          },
          snap: {
            targets: [
              interact.createSnapGrid({
                x: this.meterSize,
                y: this.meterSize,
              }),
            ],
          },
        })
        .on('resizemove', (e) => {
          const newWidth = parseFloat(this._el.style.width) + e.dx;
          const parsedWidth = (newWidth > this.meterSize) ? newWidth : this.meterSize;
          const tempSize = Math.round(parsedWidth / this.meterSize);
          this._updatePlotSize(parsedWidth);
          this._el.dataset.size = tempSize * tempSize;
          this._valid = this._isValid(this.latLng, tempSize);
          this._el.classList.add('selected-box--active');

          if (this._valid) {
            this._el.classList.remove('selected-box--error');
          } else {
            this._el.classList.add('selected-box--error');
          }
        })
        .on('resizeend', (e) => {
          this._el.classList.remove('selected-box--active');
          this._el.classList.remove('selected-box--error');
          if (this._valid) {
            this.plotSize = Math.sqrt(this._el.dataset.size);
            this.fireEvent('resizeend', e);
          } else {
            const originalWidth = this.meterSize * this.plotSize;
            this._updatePlotSize(originalWidth);
            this._el.dataset.size = this.plotSize * this.plotSize;
          }
        });
    }

    map.getPane('selectionBox').appendChild(this._el);

    this._originalElement = this._el.style;

    this.update();
  },
  onRemove(map) {
    map.getPane('selectionBox').removeChild(this._el);
  },
  getEvents() {
    return {
      zoom: this.update,
      viewreset: this.update,
    };
  },
  update() {
    this.meterSize = this._getMeterWidthInPixels();
    const pos = this._map.latLngToLayerPoint(this._latlng);
    const pixelWidth = this._getMeterWidthInPixels(this.plotSize);
    L.DomUtil.setPosition(this._el, pos);
    this._el.style.width = `${pixelWidth}px`;
    this._el.style.height = `${pixelWidth}px`;
    this._el.style.fontSize = `${pixelWidth / 2}px`;
    this._updateHandleSize();

    if (this._enableSelection) {
      interact(this._el)
        .resizable({
          snap: {
            targets: [
              interact.createSnapGrid({
                x: this.meterSize,
                y: this.meterSize,
              }),
            ],
          },
        });
    } else {
      interact(this._el).unset();
    }
  },
  _updateHandleSize() {
    if (!this._resizableHandle) return;
    this._resizableHandle.style.width = `${this.meterSize}px`;
    this._resizableHandle.style.height = `${this.meterSize}px`;
  },
  _updatePlotSize(width) {
    this._el.style.width = `${width}px`;
    this._el.style.height = `${width}px`;
    this._el.style.fontSize = `${width / 2}px`;
  },
  _getMeterWidthInPixels(distance = 1) {
    const coordSize = store.state.map.areas[this.areaId].picCoordSize;
    const destination = [this._latlng.lat + coordSize.lat * distance, this._latlng.lng];

    const startPoint = this._map.latLngToLayerPoint(this._latlng);
    const endPoint = this._map.latLngToLayerPoint(destination);

    return startPoint.distanceTo(endPoint);
  },
  getBounds(length) {
    const coordSize = store.state.map.areas[this.areaId].picCoordSize;
    const size = length || this.plotSize;
    const ne = [this._latlng.lat, this._latlng.lng + (coordSize.lon * size)];
    const sw = [this._latlng.lat - coordSize.lat * size, this._latlng.lng];

    return L.latLngBounds(sw, ne);
  },
  _isValid(latlng, size = 1) {
    const square = this.getBounds(size).pad(-0.05);

    if (!isBoundsInsideArea(square, visiblePicAreas)) return false;

    return !_.values(this._map._layers).some((layer) => {
      if (!layer._image && (!layer.plotSize || layer.plotSize && this === layer)) return false;

      const bounds = layer.getBounds();
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();

      return square.overlaps([[ne.lat, ne.lng], [sw.lat, sw.lng]]);
    });
  },
});

function selectionBox(latlng, settings) {
  return new SelectionBox(latlng, settings);
}

export default {
  template: '<div></div>',
  props: ['map', 'plotCart', 'enableSelection', 'visiblePicAreas'],
  data() {
    return {
      currentBoxes: null,
    };
  },
  methods: {
    processSelectionBoxes(plots) {
      if (this.currentBoxes) this.currentBoxes.removeFrom(this.map);
      const selectionBoxes = plots.filter(plot => plot.geoPoint)
        .map((plot) => {
          const latLng = L.latLng(plot.geoPoint);

          const selectedBox = selectionBox(latLng, { plot, enableSelection: this.enableSelection });

          if (this.enableSelection) {
            selectedBox.on('resizeend', (e) => {
              this.$store.dispatch('updatePlotInCart', {
                id: e.target.plotId,
                sideLength: Number(e.target.plotSize),
              });
            })
              .on('click', (e) => {
                this.$store.dispatch('removePlotFromCart', e.target.plotId);
              });
          }

          return selectedBox;
        });
      this.currentBoxes = L.featureGroup(selectionBoxes)
        .addTo(this.map);
    },
  },
  watch: {
    visiblePicAreas() {
      visiblePicAreas = this.visiblePicAreas;
    },
    enableSelection() {
      this.processSelectionBoxes(this.plotCart);
    },
    plotCart(plots) {
      if (this.currentBoxes) this.map.removeLayer(this.currentBoxes);

      if (plots) {
        this.processSelectionBoxes(plots);
      }
    },
    map() {
      this.selectionBoxPane = this.map.createPane('selectionBox');
      this.selectionBoxPane.style.zIndex = 500;
    },
  },
};
